import { auth } from "./setup";
import type { ActionCodeSettings } from "firebase/auth";
import {
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  signInWithEmailAndPassword,
} from "firebase/auth";

// Export auth for direct Firebase SDK access
export { auth };
import type {
  CreateMagicLinkFunction,
  ProcessMagicLinkFunction,
} from "src/types/auth";
import { logSentryError } from "src/utils/sentry";
import { API_PREFIX_PATH } from "src/constants";

/**************
 * Magic Link *
 **************/
export const createMagicLink: CreateMagicLinkFunction = (email) => {
  const path = "/dashboard";
  const url = new URL(path, __FRONTEND_BASE_URL__).toString();
  const config: ActionCodeSettings = {
    url,
    handleCodeInApp: true,
  };
  return sendSignInLinkToEmail(auth, email, config);
};

export const processMagicLink: ProcessMagicLinkFunction = (onError) => {
  if (isSignInWithEmailLink(auth, window.location.href)) {
    let email = window.localStorage.getItem("emailForSignIn");
    if (!email) {
      // https://firebase.google.com/docs/auth/web/email-link-auth#completing_sign-in_in_a_web_page
      // User opened the link on a different device. To prevent session fixation attacks, ask the user to provide the associated email again. For example:
      email = window.prompt("Please provide your email for confirmation");
    }
    // The client SDK will parse the code from the link for you.
    if (!email) {
      if (onError) {
        onError(new Error("No email provided for magic link sign-in"));
      }
      return;
    }
    signInWithEmailLink(auth, email, window.location.href)
      .then(() => {
        window.localStorage.removeItem("emailForSignIn");
        window.location.href = `/finding-user-dealer/dashboard`;
      })
      .catch((error: unknown) => {
        console.error("MAGIC LINK CONFIRMATION ERROR:", error, email);
        if (
          error instanceof Error &&
          "code" in error &&
          (error as { code: string }).code === "auth/invalid-action-code"
        ) {
          console.log("Expired magic link");
        }
        if (onError) {
          return onError(error);
        }
      });
  }
};

export const signInWithEmail: (
  email: string,
  password: string,
) => Promise<unknown> = (email, password) => {
  return signInWithEmailAndPassword(auth, email, password)
    .then(() => {
      console.info("EMAIL SIGN-IN SUCCESS", email);
      window.location.href = `/finding-user-dealer/dashboard`;
    })
    .catch((error) => {
      console.info("EMAIL SIGN-IN ERROR", error, email);
      logSentryError(
        new Error(`Email sign-in failed for email: ${email}`),
        "login-page",
        "email-sign-in-failure",
      );
      throw error;
    });
};

export const sendPasswordReset: (email: string) => Promise<void> = async (
  email,
) => {
  const continue_url = new URL("/login", __FRONTEND_BASE_URL__).toString();
  const url = new URL(
    `${API_PREFIX_PATH}/reset-password`,
    __API_BASE_URL__,
  ).toString();

  const res = await fetch(url, {
    method: "POST",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify({ email, continue_url }),
  });

  if (!res.ok) {
    console.info("PASSWORD RESET EMAIL ERROR", res.status, email);
    throw new Error(`Failed to send password reset email: ${res.status}`);
  }

  console.info("PASSWORD RESET EMAIL SENT", email);
};
